import * as React from 'react'
import { formatDistanceToNowStrict } from 'date-fns'
import { formatInTimeZone,fromZonedTime } from 'date-fns-tz'

import { getSelectedStore,subscribeSelectedStore } from '@/features/shared'

/// Fallback timezone when the selected store has none resolvable. Kept in sync
/// with the backend `DEFAULT_TIME_ZONE`.
export const DEFAULT_TIME_ZONE = 'America/New_York'

/// The currently-selected store's IANA timezone, read synchronously (no hook).
/// Use this as the default timezone for pure helpers/builders that run outside
/// React render (e.g. dialog draft builders). Components should prefer the
/// `useStoreTimeZone()` hook so they re-render on store switch.
export function getStoreTimeZone(): string {
  return getSelectedStore()?.timeZone || DEFAULT_TIME_ZONE
}

/// Render a UTC instant in the store's timezone using date-fns format tokens.
/// Use this for every user-facing wall-clock timestamp (receipts, order/sale
/// times, report rows, shift times) so they read in the store's local time
/// regardless of the browser's timezone. Do NOT use it for relative
/// "x minutes ago" / countdown displays — those are instant deltas.
/// `timeZone` defaults to the selected store's, read at render time — components
/// re-render on store switch anyway (their queries are keyed by shopId), so the
/// explicit `useStoreTimeZone()` argument is optional.
export function formatInStoreTz(
  instant: Date | string | number,
  fmt: string,
  timeZone: string = getStoreTimeZone(),
): string {
  return formatInTimeZone(new Date(instant), timeZone || DEFAULT_TIME_ZONE, fmt)
}

/// Render a DATE-ONLY value (a calendar day, not an instant) — e.g. Metrc's
/// `packagedDate` / `useByDate`, which arrive as midnight UTC ("2025-12-25T00:
/// 00:00.000Z") and stand for a day, not a moment. These must be formatted in
/// UTC: converting them to the store's timezone would roll midnight back to 7pm
/// the PREVIOUS day and show the wrong date. Use formatInStoreTz() for real
/// instants (createdAt, adjustment times, …) and this for calendar days.
export function formatDateOnly(
  value: Date | string | number | null | undefined,
  fmt: string,
  fallback = '—',
): string {
  if (value == null || value === '') return fallback
  const d = new Date(value)
  if (Number.isNaN(d.getTime())) return fallback
  return formatInTimeZone(d, 'UTC', fmt)
}

/// Interpret a `datetime-local` input value (store-local wall clock, e.g.
/// "2026-06-13T17:00") as a time in the store's timezone and return the UTC ISO
/// string to send to the backend.
export function storeWallClockToUtc(localValue: string, timeZone: string): string {
  return fromZonedTime(localValue, timeZone || DEFAULT_TIME_ZONE).toISOString()
}

/// Convert a UTC ISO instant to the "yyyy-MM-dd'T'HH:mm" store-local string a
/// `datetime-local` input expects.
export function utcToStoreWallClock(iso: string | number | Date, timeZone: string): string {
  return formatInTimeZone(new Date(iso), timeZone || DEFAULT_TIME_ZONE, "yyyy-MM-dd'T'HH:mm")
}

/// Human, contextual timestamp for lists: "Today · 4:38 PM", "Yesterday · 4:29 PM",
/// "Mon, Jun 29 · 2:16 PM" (within a week), "Jun 27 · 12:58 PM" (older; the year is
/// added when it differs from now). Rendered in the store's timezone.
export function formatSmartDate(
  instant: Date | string | number | null | undefined,
  timeZone: string = getStoreTimeZone(),
): string {
  if (instant == null) return '—'
  const tz = timeZone || DEFAULT_TIME_ZONE
  const d = new Date(instant)
  const now = new Date()
  const dayKey = (dt: Date): string => formatInTimeZone(dt, tz, 'yyyy-MM-dd')
  const dk = dayKey(d)
  const time = formatInTimeZone(d, tz, 'h:mm a')

  if (dk === dayKey(now)) return `Today · ${time}`
  if (dk === dayKey(new Date(now.getTime() - 86_400_000))) return `Yesterday · ${time}`

  const diffDays = Math.round((Date.parse(dayKey(now)) - Date.parse(dk)) / 86_400_000)
  if (diffDays >= 2 && diffDays < 7) return `${formatInTimeZone(d, tz, 'EEE, MMM d')} · ${time}`

  const sameYear = formatInTimeZone(d, tz, 'yyyy') === formatInTimeZone(now, tz, 'yyyy')
  return `${formatInTimeZone(d, tz, sameYear ? 'MMM d' : 'MMM d, yyyy')} · ${time}`
}

/// Strict "x ago" without date-fns' fuzzy "about" prefix (e.g. "1 hour ago").
export function formatRelativeStrict(instant: Date | string | number | null | undefined): string {
  if (instant == null) return ''
  return formatDistanceToNowStrict(new Date(instant), { addSuffix: true })
}

/// The selected store's IANA timezone, re-rendering on store switch. Falls back
/// to DEFAULT_TIME_ZONE when no store/timezone is selected.
export function useStoreTimeZone(): string {
  return React.useSyncExternalStore(
    subscribeSelectedStore,
    () => getSelectedStore()?.timeZone || DEFAULT_TIME_ZONE,
    () => DEFAULT_TIME_ZONE,
  )
}
